import AdminSiteLink from "admin/common/AdminSiteLink";
import Loading from "common/ui/Loading";
import React, { useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import PartnerSettings from "./partnerIntegration/PartnerSettings";
import { PillNav } from "common/ui/PillNav";
import { NavLink, Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import GeneralSettings from "./general/GeneralSettings";
import PageNotFound from "app/PageNotFound";
import ErrorBoundary from "common/ui/ErrorBoundary";
import Merchant, { MerchantFeature, isFeatureEnabled } from "domain/merchant";
import merchantService from "./merchantService";
import TraderSettings from "./traders/TraderSettings";
import RoleSettings from "./roles/RoleSettings";
import ExternalComponents from "./external/ExternalComponents";
import RiskSettings from "./risk/RiskSettings";
import MerchantDetailsComponentProps from "./merchantDetailsComponentProps";
import MobileAppSettings from "./mobileApp/MobileApp";
import Features from "./features/Features";
import ImpersonationSettings from "./impersonation/ImpersonationSettings";
import useAjaxCall from "common/ui/useAjaxCall";
import { withCancellationToken } from "admin/common/adminHttpService";
import CustomFields from "./customFields/CustomFields";

const defaultRoutes = [
    {
        name: "General",
        path: "general"
    },
    {
        name: "Risk",
        path: "risk"
    },
    {
        name: "Traders",
        path: "traders"
    },
    {
        name: "Roles",
        path: "roles"
    },
    {
        name: "Partner Integration",
        path: "partners"
    },
    {
        name: "External",
        path: "external"
    },
    {
        name: "Mobile App",
        path: "mobile"
    },
    {
        name: "Custom Fields",
        path: "custom-fields"
    },
];

const MerchantDetailsPage: React.FC<RouteComponentProps<{ merchantId: string }>> = (props) => {
    const [merchant, setMerchant] = useState<Merchant>(null);
    const merchantId = props.match.params.merchantId;

    useAjaxCall(cxt => {
        withCancellationToken(merchantService, cxt).getMerchant(merchantId)
            .then(m => setMerchant(m));
    }, []);

    const routes = [...defaultRoutes];

    if (merchant != null) {
        if (isFeatureEnabled(merchant, MerchantFeature.ThirdPartyAppIntegration)) {
            routes.push({
                name: "Impersonation Keys",
                path: "impersonation-keys"
            })
        }

        routes.push({
            name: "Features",
            path: "features"
        });
    }

    function isLoaded(): boolean {
        return merchant != null;
    }

    function getRouteName(): string {
        return routes.find(m => props.location.pathname.startsWith(m.path))?.name;
    }

    const detailProps: MerchantDetailsComponentProps = {
        merchant: merchant,
        setMerchant: setMerchant
    };

    return <div>
        <div className="row col">
            <Breadcrumb>
                <li className="breadcrumb-item"><AdminSiteLink.Home>Admin</AdminSiteLink.Home></li>
                <li className="breadcrumb-item"><AdminSiteLink.Merchants>Merchants</AdminSiteLink.Merchants></li>
                <Breadcrumb.Item active>{merchant?.name}</Breadcrumb.Item>
            </Breadcrumb>
        </div>

        <PillNav id="pages-pills" text={getRouteName()}>
            {routes.map(m =>
                <NavLink className="nav-link" to={`/admin/merchants/${merchantId}/${m.path}`} key={m.path}>
                    {m.name}
                </NavLink>)}
        </PillNav>

        <Loading isLoading={!isLoaded()}>
            {() => <Switch>
                <Redirect exact from={`/admin/merchants/${merchantId}`} to={`/admin/merchants/${merchantId}/general`} />
                <Route path={`/admin/merchants/${merchantId}/general`} render={() => <ErrorBoundary>
                    <GeneralSettings {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/risk`} render={() => <ErrorBoundary>
                    <RiskSettings {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/traders`} render={() => <ErrorBoundary>
                    <TraderSettings {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/roles`} render={() => <ErrorBoundary>
                    <RoleSettings {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/partners`} render={() => <ErrorBoundary>
                    <PartnerSettings {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/external`} render={() => <ErrorBoundary>
                    <ExternalComponents {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/mobile`} render={() => <ErrorBoundary>
                    <MobileAppSettings {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/impersonation-keys`} render={() => <ErrorBoundary>
                    <ImpersonationSettings {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/features`} render={() => <ErrorBoundary>
                    <Features {...detailProps} />
                </ErrorBoundary>} />
                <Route path={`/admin/merchants/${merchantId}/custom-fields`} render={() => <ErrorBoundary>
                    <CustomFields {...detailProps} />
                </ErrorBoundary>} />
                <Route>
                    <PageNotFound />
                </Route>
            </Switch>}
        </Loading>
    </div>
}

export default MerchantDetailsPage;